import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Globe,
  Check,
  Crown,
  Zap,
  Shield,
  ArrowRight,
  Loader2,
  CheckCircle2,
  Clock,
  Star,
  Sparkles,
  Trophy,
  RefreshCw,
  AlertCircle,
} from "lucide-react";
import { useLocationStore } from "@/lib/store";
import type { Location, CitationOrder } from "@shared/schema";
import { useEffect } from "react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

export default function CitationsPage() {
  const { selectedLocationId } = useLocationStore();
  const { toast } = useToast();

  const { data: locations } = useQuery<Location[]>({
    queryKey: ["/api/locations"],
  });

  const locationId = selectedLocationId || locations?.[0]?.id;

  const { data: orders = [], isLoading } = useQuery<CitationOrder[]>({
    queryKey: ["/api/citation-orders", locationId],
    enabled: !!locationId,
  });

  const searchParams = new URLSearchParams(window.location.search);
  // Whop redirects back with our internal ?orderId=<id>. We still accept legacy
  // ?session_id for any in-flight Stripe redirects from before the migration.
  const orderIdParam = searchParams.get("orderId");
  const sessionId = searchParams.get("session_id");
  const retryOrderId = searchParams.get("retry");

  useEffect(() => {
    if (!orderIdParam && !sessionId) return;
    const qs = orderIdParam
      ? `orderId=${encodeURIComponent(orderIdParam)}`
      : `session_id=${encodeURIComponent(sessionId!)}`;
    apiRequest("GET", `/api/citation-orders/verify-session?${qs}`)
      .then(r => r.json())
      .then((order) => {
        if (order.status === "paid") {
          toast({ title: "Payment confirmed!", description: "Your citation package has been activated." });
          queryClient.invalidateQueries({ queryKey: ["/api/citation-orders", locationId] });
        }
        window.history.replaceState({}, "", "/citations");
      })
      .catch(() => {});
  }, [orderIdParam, sessionId]);

  const checkoutMutation = useMutation({
    mutationFn: async (packageType: string) => {
      const res = await apiRequest("POST", "/api/citation-orders/checkout", {
        locationId,
        packageType,
      });
      return res.json();
    },
    onSuccess: (data) => {
      if (data.url) {
        window.location.href = data.url;
      }
    },
    onError: (err: Error) => {
      toast({ title: "Checkout failed", description: err.message, variant: "destructive" });
    },
  });

  const retryCheckoutMutation = useMutation({
    mutationFn: async (orderId: string) => {
      const res = await apiRequest("POST", `/api/citation-orders/${orderId}/retry`);
      return res.json();
    },
    onSuccess: (data) => {
      if (data.url) {
        toast({ title: "Reopening Whop checkout", description: "Sending you to a fresh secure payment page…" });
        window.location.href = data.url;
      } else {
        window.history.replaceState({}, "", "/citations");
      }
    },
    onError: (err: Error) => {
      toast({ title: "Couldn't retry checkout", description: err.message, variant: "destructive" });
      window.history.replaceState({}, "", "/citations");
    },
  });

  useEffect(() => {
    if (!retryOrderId) return;
    if (retryCheckoutMutation.isPending || retryCheckoutMutation.isSuccess || retryCheckoutMutation.isError) return;
    retryCheckoutMutation.mutate(retryOrderId);
  }, [retryOrderId]);

  const isAnnualActive = (o?: CitationOrder) => Boolean(o && o.expiresAt && new Date(o.expiresAt) > new Date());

  const standardOrder = orders.find(o => o.packageType === "standard" && o.status === "paid");
  const vaultOrder = orders.find(o => o.packageType === "vault" && o.status === "paid");
  const premiumOrder = orders.find(o => o.packageType === "premium" && o.status === "paid");
  const dominationOrder = orders.find(o => o.packageType === "domination" && o.status === "paid");

  const vaultActive = isAnnualActive(vaultOrder);
  const premiumActive = isAnnualActive(premiumOrder);
  const dominationActive = isAnnualActive(dominationOrder);

  const standardCovered = Boolean(standardOrder) || dominationActive;
  const vaultCovered = vaultActive || dominationActive;
  const premiumCovered = premiumActive || dominationActive;

  const buying = checkoutMutation.isPending ? checkoutMutation.variables : null;

  if (!locationId) {
    return (
      <div className="p-6 flex items-center justify-center h-full">
        <p className="text-muted-foreground" data-testid="text-no-location">Select a location to manage citations.</p>
      </div>
    );
  }

  if (retryOrderId && (retryCheckoutMutation.isPending || retryCheckoutMutation.isSuccess)) {
    return (
      <div className="p-6 flex flex-col items-center justify-center h-full text-center" data-testid="status-retry-checkout">
        <Loader2 className="w-8 h-8 text-primary animate-spin mb-4" />
        <h2 className="text-lg font-semibold">Reopening your checkout…</h2>
        <p className="text-sm text-muted-foreground mt-1">Hang tight while we send you back to a fresh Whop payment page.</p>
      </div>
    );
  }

  const standardFeatures = [
    "100 business directory submissions",
    "NAP consistency across all listings",
    "Top-tier directories included (Yelp, BBB, Bing, Apple Maps)",
    "Manual review & quality check",
    "Submission report delivered",
    "One-time purchase — no recurring fees",
  ];

  const vaultFeatures = [
    "150+ premium directory submissions",
    "Always-on listing monitoring",
    "Auto-detection of NAP changes & errors",
    "Duplicate suppression across the web",
    "Quarterly health audits & cleanup",
    "Annual refresh — never goes stale",
  ];

  const premiumFeatures = [
    "200+ directory submissions",
    "All Standard & Vault directories included",
    "Industry-specific & niche directories",
    "Ongoing monitoring & corrections",
    "Duplicate suppression",
    "Monthly citation health reports",
    "Priority support",
    "Auto-renewal annually",
  ];

  const orderLabel = (t: string) =>
    t === "domination" ? "Total Domination Bundle"
    : t === "premium" ? "Empire Citations (200+)"
    : t === "vault" ? "Fortress Citations (150+)"
    : "Foothold Citations (100)";

  const orderIcon = (t: string) => {
    if (t === "domination") return <Trophy className="w-4 h-4 text-[#a78bfa]" />;
    if (t === "premium") return <Crown className="w-4 h-4 text-[#f97316]" />;
    if (t === "vault") return <Sparkles className="w-4 h-4 text-emerald-400" />;
    return <Zap className="w-4 h-4 text-blue-400" />;
  };

  const orderIconBg = (t: string) =>
    t === "domination" ? "bg-[#a78bfa]/10"
    : t === "premium" ? "bg-[#f97316]/10"
    : t === "vault" ? "bg-emerald-500/10"
    : "bg-blue-500/10";

  return (
    <div className="p-6 pb-24 space-y-6 overflow-y-auto h-full">
      <div>
        <h1 className="text-2xl font-bold tracking-tight" data-testid="text-citations-title">Citations</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Build your local SEO authority with consistent business listings across the web
        </p>
      </div>

      <Card className="border-[#1E1E2E] bg-gradient-to-r from-[#12121A] to-[#1a1a2e]" data-testid="card-citations-info">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#00D4AA]/10 flex items-center justify-center shrink-0">
              <Globe className="w-6 h-6 text-[#00D4AA]" />
            </div>
            <div>
              <h2 className="text-lg font-semibold mb-1">Why Citations Matter</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Citations are mentions of your business name, address, and phone number (NAP) on other websites.
                Consistent citations across directories are a top-3 ranking factor for local SEO.
                Google uses them to validate your business and boost your visibility in the Local Pack.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Skeleton className="h-[520px]" />
          <Skeleton className="h-[520px]" />
          <Skeleton className="h-[520px]" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
            {/* STANDARD */}
            <Card
              className={`border-[#1E1E2E] bg-[#12121A] relative overflow-hidden ${standardCovered ? "ring-1 ring-emerald-500/30" : ""}`}
              data-testid="card-standard-citations"
            >
              {standardCovered && (
                <div className="absolute top-4 right-4">
                  <Badge className="bg-emerald-500/20 text-emerald-400 gap-1" data-testid="badge-standard-purchased">
                    <CheckCircle2 className="w-3 h-3" /> {dominationActive && !standardOrder ? "Included" : "Purchased"}
                  </Badge>
                </div>
              )}
              <CardContent className="p-6 flex flex-col h-full">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                    <Zap className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold" data-testid="text-standard-title">Foothold Citations</h3>
                    <p className="text-xs text-muted-foreground">Get on the map · One-time</p>
                  </div>
                </div>

                <div className="mb-6">
                  <div className="flex items-baseline gap-1">
                    <span className="text-3xl sm:text-4xl font-bold" data-testid="text-standard-price">$97</span>
                    <span className="text-sm text-muted-foreground">one-time</span>
                  </div>
                </div>

                <div className="space-y-3 mb-8 flex-1">
                  {standardFeatures.map((feature, i) => (
                    <div key={i} className="flex items-start gap-2.5">
                      <Check className="w-4 h-4 text-blue-400 mt-0.5 shrink-0" />
                      <span className="text-sm text-muted-foreground">{feature}</span>
                    </div>
                  ))}
                </div>

                {standardCovered ? (
                  <div className="space-y-3">
                    {standardOrder ? (
                      <div className="flex items-center gap-2 text-sm text-emerald-400">
                        <CheckCircle2 className="w-4 h-4" />
                        <span>Purchased on {new Date(standardOrder.paidAt!).toLocaleDateString()}</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-sm text-emerald-400">
                        <Trophy className="w-4 h-4" />
                        <span>Included in Total Domination</span>
                      </div>
                    )}
                    <Button disabled className="w-full bg-emerald-600/20 text-emerald-400 border-emerald-500/30" data-testid="button-standard-purchased">
                      <Check className="w-4 h-4 mr-2" /> Active
                    </Button>
                  </div>
                ) : (
                  <Button
                    className="w-full bg-brand-orange hover:bg-brand-orange/90 text-white font-semibold shadow-lg shadow-brand-orange/20"
                    size="lg"
                    onClick={() => checkoutMutation.mutate("standard")}
                    disabled={checkoutMutation.isPending}
                    data-testid="button-buy-standard"
                  >
                    {buying === "standard" ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <ArrowRight className="w-4 h-4 mr-2" />
                    )}
                    Get on the map — Foothold
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* VAULT — best value */}
            <Card
              className={`relative overflow-hidden lg:scale-[1.02] lg:-my-1 ${vaultCovered ? "border-emerald-500/30 ring-1 ring-emerald-500/30 bg-[#12121A]" : "border-emerald-500/40 bg-gradient-to-b from-[#0f1a17] to-[#12121A] shadow-[0_0_40px_-15px_rgba(16,185,129,0.4)]"}`}
              data-testid="card-vault-citations"
            >
              {!vaultCovered && (
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-500 via-teal-400 to-emerald-500" />
              )}
              <div className="absolute top-4 right-4">
                {vaultCovered ? (
                  <Badge className="bg-emerald-500/20 text-emerald-400 gap-1" data-testid="badge-vault-active">
                    <CheckCircle2 className="w-3 h-3" /> {dominationActive && !vaultActive ? "Included" : "Active"}
                  </Badge>
                ) : (
                  <Badge className="bg-emerald-500/20 text-emerald-400 gap-1 border border-emerald-500/40" data-testid="badge-vault-best-value">
                    <Sparkles className="w-3 h-3" /> Best Value
                  </Badge>
                )}
              </div>

              <CardContent className="p-6 flex flex-col h-full">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold" data-testid="text-vault-title">Fortress Citations</h3>
                    <p className="text-xs text-muted-foreground">Defend the position · 150+ monitored · Annual</p>
                  </div>
                </div>

                <div className="mb-6">
                  <div className="flex items-baseline gap-1">
                    <span className="text-3xl sm:text-4xl font-bold" data-testid="text-vault-price">$199</span>
                    <span className="text-sm text-muted-foreground">/ year</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Just <span className="text-emerald-400 font-semibold">$16.58/mo</span> — half the cost, monitored daily
                  </p>
                </div>

                <div className="space-y-3 mb-8 flex-1">
                  {vaultFeatures.map((feature, i) => (
                    <div key={i} className="flex items-start gap-2.5">
                      <Check className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                      <span className="text-sm text-muted-foreground">{feature}</span>
                    </div>
                  ))}
                </div>

                {vaultCovered ? (
                  <div className="space-y-3">
                    {vaultActive ? (
                      <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2 text-emerald-400">
                          <Shield className="w-4 h-4" />
                          <span>Active since {new Date(vaultOrder!.paidAt!).toLocaleDateString()}</span>
                        </div>
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <Clock className="w-3.5 h-3.5" />
                          <span>Renews {new Date(vaultOrder!.expiresAt!).toLocaleDateString()}</span>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-sm text-emerald-400">
                        <Trophy className="w-4 h-4" />
                        <span>Included in Total Domination</span>
                      </div>
                    )}
                    <Button disabled className="w-full bg-emerald-600/20 text-emerald-400 border-emerald-500/30" data-testid="button-vault-active">
                      <Shield className="w-4 h-4 mr-2" /> Vault Active
                    </Button>
                  </div>
                ) : (
                  <Button
                    className="w-full bg-brand-orange hover:bg-brand-orange/90 text-white font-semibold shadow-lg shadow-brand-orange/20"
                    size="lg"
                    onClick={() => checkoutMutation.mutate("vault")}
                    disabled={checkoutMutation.isPending}
                    data-testid="button-buy-vault"
                  >
                    {buying === "vault" ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Sparkles className="w-4 h-4 mr-2" />
                    )}
                    Defend the position — Fortress
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* PREMIUM */}
            <Card
              className={`relative overflow-hidden ${premiumCovered ? "border-emerald-500/30 ring-1 ring-emerald-500/30 bg-[#12121A]" : "border-[#f97316]/30 bg-gradient-to-b from-[#1a1510] to-[#12121A]"}`}
              data-testid="card-premium-citations"
            >
              {!premiumCovered && (
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#f97316] via-[#f59e0b] to-[#f97316]" />
              )}
              <div className="absolute top-4 right-4">
                {premiumCovered ? (
                  <Badge className="bg-emerald-500/20 text-emerald-400 gap-1" data-testid="badge-premium-active">
                    <CheckCircle2 className="w-3 h-3" /> {dominationActive && !premiumActive ? "Included" : "Active"}
                  </Badge>
                ) : (
                  <Badge className="bg-[#f97316]/20 text-[#f97316] gap-1" data-testid="badge-premium-popular">
                    <Star className="w-3 h-3" /> Most Coverage
                  </Badge>
                )}
              </div>

              <CardContent className="p-6 flex flex-col h-full">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-[#f97316]/10 flex items-center justify-center">
                    <Crown className="w-5 h-5 text-[#f97316]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold" data-testid="text-premium-title">Empire Citations</h3>
                    <p className="text-xs text-muted-foreground">Rule the territory · 200+ sites · Annual</p>
                  </div>
                </div>

                <div className="mb-6">
                  <div className="flex items-baseline gap-1">
                    <span className="text-3xl sm:text-4xl font-bold" data-testid="text-premium-price">$399</span>
                    <span className="text-sm text-muted-foreground">/ year</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    That's only <span className="text-[#f97316] font-semibold">$33.25/mo</span> for full coverage
                  </p>
                </div>

                <div className="space-y-3 mb-8 flex-1">
                  {premiumFeatures.map((feature, i) => (
                    <div key={i} className="flex items-start gap-2.5">
                      <Check className="w-4 h-4 text-[#f97316] mt-0.5 shrink-0" />
                      <span className="text-sm text-muted-foreground">{feature}</span>
                    </div>
                  ))}
                </div>

                {premiumCovered ? (
                  <div className="space-y-3">
                    {premiumActive ? (
                      <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2 text-emerald-400">
                          <Shield className="w-4 h-4" />
                          <span>Active since {new Date(premiumOrder!.paidAt!).toLocaleDateString()}</span>
                        </div>
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <Clock className="w-3.5 h-3.5" />
                          <span>Renews {new Date(premiumOrder!.expiresAt!).toLocaleDateString()}</span>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-sm text-emerald-400">
                        <Trophy className="w-4 h-4" />
                        <span>Included in Total Domination</span>
                      </div>
                    )}
                    <Button disabled className="w-full bg-emerald-600/20 text-emerald-400 border-emerald-500/30" data-testid="button-premium-active">
                      <Shield className="w-4 h-4 mr-2" /> Premium Active
                    </Button>
                  </div>
                ) : (
                  <Button
                    className="w-full bg-brand-orange hover:bg-brand-orange/90 text-white font-semibold shadow-lg shadow-brand-orange/20"
                    size="lg"
                    onClick={() => checkoutMutation.mutate("premium")}
                    disabled={checkoutMutation.isPending}
                    data-testid="button-buy-premium"
                  >
                    {buying === "premium" ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Crown className="w-4 h-4 mr-2" />
                    )}
                    Rule the territory — Empire
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>

          {/* TOTAL DOMINATION BUNDLE */}
          <Card
            className={`relative overflow-hidden ${dominationActive ? "border-emerald-500/30 ring-1 ring-emerald-500/30 bg-[#12121A]" : "border-[#a78bfa]/40 bg-gradient-to-r from-[#1a1230] via-[#12121A] to-[#1a1230] shadow-[0_0_50px_-20px_rgba(167,139,250,0.5)]"}`}
            data-testid="card-domination-bundle"
          >
            {!dominationActive && (
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#a78bfa] via-[#ec4899] to-[#f97316]" />
            )}
            <CardContent className="p-6">
              <div className="flex flex-col lg:flex-row items-start lg:items-center gap-6">
                <div className="flex items-center gap-4 flex-1">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#a78bfa]/20 to-[#f97316]/20 flex items-center justify-center shrink-0">
                    <Trophy className="w-7 h-7 text-[#a78bfa]" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-xl font-bold" data-testid="text-domination-title">Total Domination Bundle</h3>
                      {dominationActive ? (
                        <Badge className="bg-emerald-500/20 text-emerald-400 gap-1" data-testid="badge-domination-active">
                          <CheckCircle2 className="w-3 h-3" /> Active
                        </Badge>
                      ) : (
                        <Badge className="bg-gradient-to-r from-[#a78bfa]/20 to-[#f97316]/20 text-[#a78bfa] border border-[#a78bfa]/40 gap-1" data-testid="badge-domination-save">
                          Save $199
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground" data-testid="text-domination-subtitle">
                      The whole territory ladder, locked in. Foothold + Fortress + Empire = total local SEO authority.
                    </p>
                  </div>
                </div>

                <div className="flex flex-col items-start lg:items-end gap-1 shrink-0">
                  {!dominationActive && (
                    <span className="text-xs text-muted-foreground line-through" data-testid="text-domination-list-price">
                      List $698/yr
                    </span>
                  )}
                  <div className="flex items-baseline gap-1">
                    <span className="text-3xl font-bold" data-testid="text-domination-price">$499</span>
                    <span className="text-sm text-muted-foreground">/ year</span>
                  </div>
                </div>

                <div className="w-full lg:w-auto shrink-0">
                  {dominationActive ? (
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2 text-emerald-400">
                        <Shield className="w-4 h-4" />
                        <span>Active since {new Date(dominationOrder!.paidAt!).toLocaleDateString()}</span>
                      </div>
                      <div className="flex items-center gap-1 text-muted-foreground text-xs">
                        <Clock className="w-3.5 h-3.5" />
                        <span>Renews {new Date(dominationOrder!.expiresAt!).toLocaleDateString()}</span>
                      </div>
                    </div>
                  ) : (
                    <Button
                      className="w-full lg:w-auto bg-brand-orange hover:bg-brand-orange/90 text-white font-semibold shadow-lg shadow-brand-orange/20"
                      size="lg"
                      onClick={() => checkoutMutation.mutate("domination")}
                      disabled={checkoutMutation.isPending}
                      data-testid="button-buy-domination"
                    >
                      {buying === "domination" ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Trophy className="w-4 h-4 mr-2" />
                      )}
                      Get Total Domination
                    </Button>
                  )}
                </div>
              </div>

              {!dominationActive && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-5 pt-5 border-t border-[#1E1E2E]">
                  <div className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-blue-400" />
                    <span className="text-muted-foreground">100 one-time citations</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-emerald-400" />
                    <span className="text-muted-foreground">150+ monitored Fortress sites</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-[#f97316]" />
                    <span className="text-muted-foreground">200+ Empire directories</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {(() => {
        const visibleOrders = orders.filter((order) => {
          if (order.status !== "abandoned") return true;
          const orderTime = order.createdAt ? new Date(order.createdAt).getTime() : 0;
          const supersededBy = orders.find((o) =>
            o.id !== order.id &&
            o.packageType === order.packageType &&
            (o.createdAt ? new Date(o.createdAt).getTime() : 0) > orderTime,
          );
          return !supersededBy;
        });
        if (visibleOrders.length === 0) return null;
        return (
        <Card className="border-[#1E1E2E] bg-[#12121A]" data-testid="card-order-history">
          <CardContent className="p-6">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-4">Order History</h3>
            <div className="space-y-3">
              {visibleOrders.map((order) => {
                const isAbandoned = order.status === "abandoned";
                const isPaid = order.status === "paid";
                const badgeClass = isPaid
                  ? "bg-emerald-500/20 text-emerald-400"
                  : isAbandoned
                    ? "bg-rose-500/20 text-rose-400 border border-rose-500/30"
                    : "bg-amber-500/20 text-amber-400";
                const badgeLabel = isPaid
                  ? "Paid"
                  : isAbandoned
                    ? "Checkout expired"
                    : "Pending";
                return (
                  <div
                    key={order.id}
                    className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 rounded-lg bg-[#0a0a12] border ${isAbandoned ? "border-rose-500/30" : "border-[#1E1E2E]"}`}
                    data-testid={`row-order-${order.id}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${orderIconBg(order.packageType)}`}>
                        {orderIcon(order.packageType)}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">
                          {orderLabel(order.packageType)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(order.createdAt!).toLocaleDateString()}
                          {isAbandoned && (
                            <span className="ml-2 inline-flex items-center gap-1 text-rose-400">
                              <AlertCircle className="w-3 h-3" />
                              You closed checkout before payment
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 sm:justify-end">
                      <span className="text-sm font-semibold">${(order.amount / 100).toFixed(0)}</span>
                      <Badge
                        className={badgeClass}
                        data-testid={`badge-order-status-${order.id}`}
                      >
                        {badgeLabel}
                      </Badge>
                      {isAbandoned && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 border-rose-500/40 bg-rose-500/10 text-rose-300 hover:bg-rose-500/20 hover:text-rose-200"
                          onClick={() => checkoutMutation.mutate(order.packageType)}
                          disabled={checkoutMutation.isPending}
                          data-testid={`button-retry-order-${order.id}`}
                        >
                          {buying === order.packageType ? (
                            <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                          ) : (
                            <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                          )}
                          Start over
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
        );
      })()}
    </div>
  );
}
